import { motion } from "framer-motion";
import {
  Workflow, ShoppingBag, Braces, Zap, CreditCard, Cloud, Megaphone, ArrowUpRight,
} from "lucide-react";

// Dark, service-matched Unsplash imagery. All read as very dark scenes so the
// text stays legible with only a subtle overlay.
const SERVICES = [
  {
    n: "01",
    title: "Custom CRM & Business Automation",
    desc: "Tailored CRM systems and workflow automation for sales, support and operations teams. Built to match how your business actually runs.",
    Icon: Workflow,
    span: "md:col-span-4 md:row-span-2",
    // Dark analytics dashboard
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1400&q=80",
    imgPos: "object-center",
  },
  {
    n: "02",
    title: "Headless & Custom eCommerce",
    desc: "Scalable storefronts, custom checkout flows and multi-vendor platforms — engineered for conversion and edge performance.",
    Icon: ShoppingBag,
    span: "md:col-span-2",
    // Dark shopping / product photography
    img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1200&q=80",
    imgPos: "object-center",
  },
  {
    n: "03",
    title: "RESTful & GraphQL APIs",
    desc: "Robust, versioned, well-documented APIs powering web, mobile and third-party integrations at scale.",
    Icon: Braces,
    span: "md:col-span-2",
    // Dark code editor / terminal
    img: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1200&q=80",
    imgPos: "object-center",
  },
  {
    n: "04",
    title: "Workflow Automation & No-Code",
    desc: "Zapier-based automation connecting your business tools without manual work.",
    Icon: Zap,
    span: "md:col-span-2",
    // Dark circuit / interconnected nodes
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1200&q=80",
    imgPos: "object-center",
  },
  {
    n: "05",
    title: "Secure Payment Gateways",
    desc: "PCI-compliant integrations with Stripe, PayPal, Authorize.net and NMI — including tokenization, 3DS and recurring billing.",
    Icon: CreditCard,
    span: "md:col-span-2",
    // Dark credit card / payment terminal
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1200&q=80",
    imgPos: "object-center",
  },
  {
    n: "06",
    title: "Cloud-Native AWS & DevOps",
    desc: "Scalable, secure, cloud-first architecture on AWS with IaC, CI/CD and observability from day one.",
    Icon: Cloud,
    span: "md:col-span-2",
    // Dark server room / infrastructure
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1200&q=80",
    imgPos: "object-center",
  },
  {
    n: "07",
    title: "GoHighLevel Sales & Marketing Automation",
    desc: "Certified GoHighLevel builds — funnels, pipelines, SMS/email workflows and white-label sub-account management for agencies scaling client operations.",
    Icon: Megaphone,
    span: "md:col-span-6",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=webp&auto=format,compress&w=1600&q=80",
    imgPos: "object-center",
    featured: true,
  },
];

const GHL_CERT_URL = "https://directory.gohighlevel.com/india/pune/certified-admins/kiran-kshirsagar";
const GHL_BADGE_URL = "https://customer-assets-agu9un31.emergentagent.net/job_beacon-tech-labs/artifacts/6b4y1mad_09d68805-ef5b-4f16-9b67-ccce0bf65473.webp";

function ServiceCard({ s, i }) {
  const { Icon } = s;
  const isExternal = !!s.featured;
  return (
    <motion.a
      href={isExternal ? GHL_CERT_URL : "#contact"}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      data-testid={`service-card-${s.n}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.6, delay: i * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
      className={`group relative overflow-hidden border border-hairline bg-surface transition-colors p-6 md:p-8 flex flex-col justify-between ${s.span}`}
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <img
          src={s.img}
          alt=""
          loading="lazy"
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover opacity-[0.75] group-hover:opacity-[0.9] group-hover:scale-[1.04] transition-all duration-[700ms] ease-[cubic-bezier(.2,.7,.2,1)] ${s.imgPos}`}
        />
        {/* Dark tint for legibility */}
        <div className="absolute inset-0 bg-[#080A0F]/30 group-hover:bg-[#080A0F]/20 transition-colors" />
        {/* Bottom gradient to protect body copy */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#080A0F] via-[#080A0F]/70 to-[#080A0F]/10" />
        {/* Accent radial on hover */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              "radial-gradient(60% 55% at 30% 40%, rgba(255,59,48,0.16), transparent 70%)",
          }}
        />
      </div>

      {/* Top row */}
      <div className="relative z-10 flex items-start justify-between">
        <span className="mono-label !text-white/80">[{s.n}]</span>
        <ArrowUpRight
          size={20}
          className="text-white/50 group-hover:text-[color:var(--accent)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 mt-6">
        <Icon strokeWidth={1.25} className="text-[color:var(--accent)] mb-4 md:mb-5" size={28} />
        <h3 className="font-display text-[20px] md:text-[24px] lg:text-[26px] leading-[1.1] tracking-tight">
          {s.title}
        </h3>
        <p className="mt-3 md:mt-4 text-white/70 text-[13px] md:text-[14px] leading-[1.55]">
          {s.desc}
        </p>
        {s.featured && (
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <img
              src={GHL_BADGE_URL}
              alt="HighLevel Certified Admin"
              className="h-16 md:h-20 w-auto drop-shadow-[0_6px_20px_rgba(0,0,0,0.55)]"
              loading="lazy"
            />
            <div className="inline-flex items-center gap-2 border border-[color:var(--accent)]/50 bg-[color:var(--accent)]/[0.08] px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-white">
                HighLevel Certified Admin — verify ↗
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom hairline hover accent */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[color:var(--accent)]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.a>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
          <div className="md:col-span-3">
            <span className="mono-label">[02] Services</span>
          </div>
          <div className="md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98] max-w-[18ch]"
            >
              Seven engineering pillars.
              <br />
              <span className="text-white/40">One delivering partner.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 md:auto-rows-[minmax(320px,auto)] gap-4">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.n} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
